import React, { useMemo } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import DataDisplay, { DataDisplayVariant } from '@sharedCR/coreDesignSystem/DataDisplay';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { getValueOrDash } from 'utils/data';
import { hasPropertyChanged } from 'utils/loanApplication';
import { getFieldPendingChangeAuthors } from 'utils/pendingChangesAuthors';

import { getClosingInformationCardsConfig } from '../../LoanClosingCDS.config';
import { getOverviewWrapperStyles } from '../../LoanClosingCDS.styles';
import { getClosingCompanyInfo } from '../../LoanClosingCDS.utils';
import IconGroupPanel from '../IconGroupPanel';

import { getIconGroupsConfig } from './ClosingInformationSection.config';
import {
  CLOSING_INFORMATION_SECTION_TITLE,
  SPECIAL_ENDORSEMENT_LABEL,
  SPECIAL_ENDORSEMENT_PENDING_KEY,
} from './ClosingInformationSection.constants';
import {
  specialEndorsementValueStyles,
  specialEndorsementWrapperStyles,
} from './ClosingInformationSection.styles';

const ClosingInformationSection = ({
  applicationId,
  closingInfo,
  closingCompanies,
  pendingChangesData,
  companyPendingChanges,
  locationPendingChanges,
  fieldAuthors = null,
  companyAuthors = null,
  locationAuthors = null,
  isTwoPartClosing = false,
  isUnsecured = false,
  isEditAllowed = false,
  isMenuOpened = false,
  onMenuClick,
}) => {
  const cardsConfig = useMemo(
    () => getClosingInformationCardsConfig({ closingInfo, isTwoPartClosing, isUnsecured }),
    [closingInfo, isTwoPartClosing, isUnsecured],
  );

  const companyInfo = useMemo(() => getClosingCompanyInfo(closingCompanies), [closingCompanies]);

  const iconGroups = useMemo(
    () =>
      getIconGroupsConfig({
        applicationId,
        companyInfo,
        companyPendingChanges,
        locationPendingChanges,
        companyAuthors,
        locationAuthors,
        isUnsecured,
      }),
    [
      applicationId,
      companyInfo,
      companyPendingChanges,
      locationPendingChanges,
      companyAuthors,
      locationAuthors,
      isUnsecured,
    ],
  );

  const isSpecialEndorsementModified = hasPropertyChanged(
    _.get(pendingChangesData, SPECIAL_ENDORSEMENT_PENDING_KEY),
  );

  return (
    <PageBlock
      withAction
      isActionDisabled={!isEditAllowed}
      isMenuOpened={isMenuOpened}
      title={CLOSING_INFORMATION_SECTION_TITLE}
      onMenuClick={onMenuClick}
    >
      <Box sx={getOverviewWrapperStyles(cardsConfig.length)}>
        {cardsConfig.map(({ id, name, value, icon, iconColor, pendingChangesKey }) => (
          <DataDisplay
            icon={icon}
            iconColor={iconColor}
            isModified={hasPropertyChanged(_.get(pendingChangesData, pendingChangesKey))}
            key={id}
            pendingChangeAuthors={getFieldPendingChangeAuthors(fieldAuthors, pendingChangesKey)}
            title={name}
            value={value}
            variant={DataDisplayVariant.STYLE_B}
          />
        ))}
      </Box>

      <Divider />

      <IconGroupPanel groups={iconGroups} />

      {!isUnsecured && (
        <>
          <Divider />
          <Box sx={specialEndorsementWrapperStyles}>
            <Typography color="text.secondary" variant="regular14">
              {SPECIAL_ENDORSEMENT_LABEL}
            </Typography>
            <PendingChangeWrapper
              isSmall
              authors={getFieldPendingChangeAuthors(fieldAuthors, SPECIAL_ENDORSEMENT_PENDING_KEY)}
              fieldLabel={SPECIAL_ENDORSEMENT_LABEL}
              pendingChangesActionType={
                isSpecialEndorsementModified
                  ? PendingChangesActionType.MODIFIED
                  : PendingChangesActionType.NO_CHANGES
              }
            >
              <Typography sx={specialEndorsementValueStyles} variant="regular14">
                {getValueOrDash(closingInfo?.specialEndorsement)}
              </Typography>
            </PendingChangeWrapper>
          </Box>
        </>
      )}
    </PageBlock>
  );
};

ClosingInformationSection.propTypes = {
  applicationId: PropTypes.string,
  closingCompanies: PropTypes.array,
  closingInfo: PropTypes.object,
  companyAuthors: PropTypes.object,
  companyPendingChanges: PropTypes.object,
  fieldAuthors: PropTypes.object,
  isEditAllowed: PropTypes.bool,
  isMenuOpened: PropTypes.bool,
  isTwoPartClosing: PropTypes.bool,
  isUnsecured: PropTypes.bool,
  locationAuthors: PropTypes.object,
  locationPendingChanges: PropTypes.object,
  pendingChangesData: PropTypes.object,
  onMenuClick: PropTypes.func,
};

export default ClosingInformationSection;
